// src/components/sidebar/Sidebar.jsx

import { NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
import logo from "../../assets/img/logo.png";

import {
    DashboardOutlined,
    UserOutlined,
    SettingOutlined,
    LogoutOutlined,
    TransactionOutlined,
    HistoryOutlined,
    CustomerServiceOutlined,
    GiftOutlined,
    WalletOutlined,
} from "@ant-design/icons";
import { Tooltip, Avatar, Dropdown, Menu } from "antd";
import {
    AwardIcon,
    BoxIcon,
    Coins,
    DiamondIcon,
    HandCoins,
    Layers2Icon,
    ListOrdered,
    MessageSquare,
    ShieldCheckIcon,
    HelpCircle,
    Sparkles,
    PanelLeftClose,
    PanelLeft,
    Blocks,
} from "lucide-react";
import profileApi from "../../api/profileApi";

function cn(...classes) {
    return classes.filter(Boolean).join(" ");
}

function NavItem({ to, icon, label, title, collapsed }) {
    return (
        <Tooltip title={title} placement="right" open={collapsed ? undefined : false}>
            <NavLink to={to} className="block">
                {({ isActive }) => (
                    <div
                        className={cn(
                            "group flex items-center gap-3 rounded-xl px-2 py-2 transition-all duration-200",
                            "border",
                            isActive
                                ? "border-white/12 bg-white/[0.09] text-white/95 shadow-[0_8px_32px_-14px_rgba(0,0,0,0.85)]"
                                : "border-transparent text-white/55 hover:border-white/[0.08] hover:bg-white/[0.05] hover:text-white/85"
                        )}
                    >
                        <span
                            className={cn(
                                "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border transition-colors duration-200",
                                isActive
                                    ? "border-emerald-400/35 bg-emerald-500/15 text-emerald-100"
                                    : "border-white/10 bg-white/[0.05] text-white/75 group-hover:border-white/15 group-hover:bg-white/[0.07] group-hover:text-white/90"
                            )}
                        >
                            {icon}
                        </span>
                        {!collapsed && (
                            <span className="truncate text-[13px] font-medium tracking-tight">{label}</span>
                        )}
                        {isActive && !collapsed && (
                            <span className="ml-auto h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400/90 shadow-[0_0_10px_rgba(52,211,153,0.45)]" />
                        )}
                    </div>
                )}
            </NavLink>
        </Tooltip>
    );
}

const Sidebar = () => {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [profile, setProfile] = useState({});

    const fetchProfile = async () => {
        try {
            const profileData = await profileApi.myProfile();
            setProfile(profileData);
        } catch (error) {
            console.error("Error fetching profile:", error);
        }
    };

    useEffect(() => {
        fetchProfile();
    }, []);

    useEffect(() => {
        document.documentElement.style.setProperty("--sidebar-width", isCollapsed ? "5rem" : "16rem");
    }, [isCollapsed]);

    const getRoleIcon = (role) => {
        switch (role) {
            case "admin":
                return <ShieldCheckIcon className="h-5 w-5 text-emerald-300" />;
            case "support":
                return <CustomerServiceOutlined className="h-5 w-5 text-emerald-400/90" />;
            default:
                return <UserOutlined className="h-5 w-5 text-white/50" />;
        }
    };

    const exit = () => {
        localStorage.removeItem("jwtToken");
        localStorage.removeItem("role");
        window.location.href = "/login";
    };

    const profileMenu = (
        <Menu className="!rounded-xl !border !border-white/10 !bg-[#0d111c]/95 !p-1 !shadow-[0_18px_60px_-24px_rgba(0,0,0,0.9)] [&_.ant-dropdown-menu-item]:!rounded-lg">
            <Menu.Item key="1" icon={<SettingOutlined className="!text-white/70" />}>
                <NavLink to="/settings" className="!text-white/85">
                    Настройки
                </NavLink>
            </Menu.Item>
            <Menu.Item key="2" icon={<LogoutOutlined className="!text-rose-300/90" />} danger onClick={exit}>
                Выход
            </Menu.Item>
        </Menu>
    );

    const navItems = [
        { to: "/dashboard", title: "Dashboard", label: "Dashboard", icon: <DashboardOutlined className="text-lg" /> },
        { to: "/users", title: "Users", label: "Users", icon: <UserOutlined className="text-lg" /> },
        { to: "/promos", title: "Promos", label: "Promos", icon: <GiftOutlined className="text-lg" /> },
        { to: "/tasks", title: "Tasks", label: "Tasks", icon: <ListOrdered className="h-[1.125rem] w-[1.125rem]" /> },
        {
            to: "/default-tasks",
            title: "Default Tasks",
            label: "Default Tasks",
            icon: <ShieldCheckIcon className="h-[1.125rem] w-[1.125rem]" />,
        },
        { to: "/awards", title: "Awards", label: "Awards", icon: <AwardIcon className="h-[1.125rem] w-[1.125rem]" /> },
        { to: "/wallets", title: "Wallets", label: "Wallets", icon: <WalletOutlined className="text-lg" /> },
        { to: "/chat", title: "Chat", label: "Chat", icon: <MessageSquare className="h-[1.125rem] w-[1.125rem]" /> },
        { to: "/faq", title: "FAQ", label: "FAQ", icon: <HelpCircle className="h-[1.125rem] w-[1.125rem]" /> },
        {
            to: "/transactions",
            title: "Transactions",
            label: "Transactions",
            icon: <TransactionOutlined className="text-lg" />,
        },
        { to: "/fee", title: "Fee", label: "Fee", icon: <TransactionOutlined className="text-lg" /> },
        { to: "/bonus", title: "Bonus", label: "Bonus", icon: <Sparkles className="h-[1.125rem] w-[1.125rem]" /> },
        { to: "/payment", title: "Payments", label: "Payment", icon: <HandCoins className="h-[1.125rem] w-[1.125rem]" /> },
        { to: "/crystals", title: "Crystals", label: "Crystals", icon: <DiamondIcon className="h-[1.125rem] w-[1.125rem]" /> },
        {
            to: "/position-settings",
            title: "Position Settings",
            label: "PositionSettings",
            icon: <Layers2Icon className="h-[1.125rem] w-[1.125rem]" />,
        },
        { to: "/inventory", title: "Inventory", label: "Inventory", icon: <BoxIcon className="h-[1.125rem] w-[1.125rem]" /> },
        { to: "/token", title: "Token", label: "Token", icon: <Coins className="h-[1.125rem] w-[1.125rem]" /> },
        { to: "/blockchain", title: "Blockchain", label: "Blockchain", icon: <Blocks className="h-[1.125rem] w-[1.125rem]" /> },
        {
            to: "/actions",
            title: "Recent Actions",
            label: "Recent Actions",
            icon: <HistoryOutlined className="text-lg" />,
        },
    ];

    return (
        <div
            className={cn(
                "fixed left-0 top-0 z-20 flex h-screen flex-col overflow-hidden border-r border-white/10 font-poppins transition-all duration-300",
                "bg-[#070A12]/92 backdrop-blur-2xl",
                "shadow-[6px_0_48px_-18px_rgba(0,0,0,0.75)]",
                isCollapsed ? "w-20" : "w-64"
            )}
        >
            <div className="pointer-events-none absolute inset-0 overflow-hidden">
                <div className="absolute -left-16 -top-20 h-48 w-48 rounded-full bg-indigo-500/15 blur-[72px]" />
                <div className="absolute -bottom-24 left-0 h-56 w-56 rounded-full bg-fuchsia-500/12 blur-[80px]" />
            </div>

            <div className="relative z-10 flex h-full min-h-0 flex-col">
                {/* Brand */}
                <header className="shrink-0 border-b border-white/10 px-3 py-4">
                    <div className={cn("flex items-center gap-2", isCollapsed ? "flex-col" : "flex-row")}>
                        <div className="relative flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-white/10 bg-white/[0.06] shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
                            <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/20 to-transparent" />
                            <img src={logo} alt="" className="relative z-[1] h-8 w-8 rounded-full object-cover" />
                        </div>
                        {!isCollapsed && (
                            <div className="min-w-0 flex-1">
                                <h2 className="truncate text-[15px] font-semibold tracking-tight text-white/90">OctoAdmin</h2>
                                <p className="truncate text-[11px] text-white/45">Control panel</p>
                            </div>
                        )}
                        <button
                            type="button"
                            onClick={() => setIsCollapsed((c) => !c)}
                            aria-label={isCollapsed ? "Развернуть меню" : "Свернуть меню"}
                            className={cn(
                                "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.05] text-white/70 transition-colors",
                                "hover:border-white/15 hover:bg-white/[0.08] hover:text-white/90",
                                isCollapsed && "mt-1"
                            )}
                        >
                            {isCollapsed ? (
                                <PanelLeft className="h-4 w-4" />
                            ) : (
                                <PanelLeftClose className="h-4 w-4" />
                            )}
                        </button>
                    </div>
                </header>

                <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto px-2 py-3">
                    {navItems.map((item) => (
                        <NavItem key={item.to} {...item} collapsed={isCollapsed} />
                    ))}
                </nav>

                <footer className="shrink-0 border-t border-white/10 p-3">
                    <Dropdown overlay={profileMenu} placement="topLeft" trigger={["click"]}>
                        <div
                            className={cn(
                                "cursor-pointer rounded-2xl border border-white/10 bg-white/[0.04] p-3 transition-colors",
                                "hover:border-white/[0.14] hover:bg-white/[0.06]",
                                "shadow-[0_12px_40px_-28px_rgba(0,0,0,0.9)]"
                            )}
                        >
                            <div className={cn("flex items-center gap-3", isCollapsed && "justify-center")}>
                                <div className="relative shrink-0">
                                    <Avatar
                                        src={logo}
                                        className="!h-10 !w-10 border border-white/10 !bg-white/[0.06]"
                                    />
                                    <div className="absolute -right-0.5 -top-0.5 flex h-5 w-5 items-center justify-center rounded-full border border-white/10 bg-[#070A12]">
                                        {getRoleIcon(profile.role)}
                                    </div>
                                </div>
                                {!isCollapsed && (
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate text-[13px] font-medium text-white/90">
                                            {profile.firstName} {profile.lastName}
                                        </p>
                                        <p className="truncate text-[11px] capitalize text-white/45">{profile.role}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Dropdown>
                </footer>
            </div>
        </div>
    );
};

export default Sidebar;
